import { Grid } from "@mui/material";
import MxFormSubTitleAndDescription from "@rf-l1/page/form/MxFormSubTitleAndDescription";
import { useCallback } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { array, boolean, InferType, object, string } from "yup";
import { SchemaItemGeneric } from "@rf/schemas/item-generic.shema";
import { HGetKeyEnums } from "@rf-helper/generat-enums-from-keys";
import useModal from "@rf/base/modal/useModal";
import MxButton2 from "@rf-l1/buttons/MxButton2";
import FeatureEnvironment from "./FeatureEnvironment";
import FeaturesDimensions from "./FeaturesDimensions";
import Prices from "./Prices";
import FeaturesAndAmenities from "./FeaturesAndAmenities";
import {
  convertUnitStateToSchema,
  convertSchemaToUnitState
} from "./adapters/unitStateAdapter";
import BuildingUnitCommissions from "./BuildingUnitCommissions";
const BuildingUnitFormModal = () => {
  const { getData, onConfirm } = useModal();
  const data = getData() as any;

  const unitState = data.getValues(data.unitName);
  const state = convertUnitStateToSchema(unitState);
  const FORM_CONFIG = useForm<IModalSchema>({
    defaultValues: {
      ...CModalSchema
    },
    values: state,
    mode: "onBlur",
    resolver: yupResolver(ModalSchema),
    criteriaMode: "all",
    reValidateMode: "onBlur",
    delayError: 1000
  });

  const checkKeyDown = (e: any) => {
    if (e.key === "Enter") e.preventDefault();
  };

  const onSubmit = useCallback((v: IModalSchema) => {
    data.setValue(data.unitName, convertSchemaToUnitState(v));
    onConfirm();
  }, []);

  return (
    <FormProvider {...FORM_CONFIG}>
      <form
        onSubmit={FORM_CONFIG.handleSubmit(onSubmit)}
        onKeyDown={e => checkKeyDown(e)}
      >
        <Grid container rowGap={2} px={1}>
          <Grid item xs={12}>
            <MxFormSubTitleAndDescription
              label="Descripción de la unidad"
              description="Todos los datos son importantes para lograr un buen posicionamiento del proyecto."
              isRequired={false}
            />
          </Grid>

          <Grid item xs={12}>
            <FeatureEnvironment />
          </Grid>

          <Grid item xs={12}>
            <FeaturesDimensions />
          </Grid>

          <Grid item xs={12}>
            <BuildingUnitCommissions />
          </Grid>

          <Grid item xs={12}>
            <Prices />
          </Grid>

          <Grid item xs={12}>
            <FeaturesAndAmenities />
          </Grid>

          <Grid container justifyContent="center" item xs={12}>
            <MxButton2
              type="submit"
              variant="contained"
              sx={{ padding: "0.2rem 1.2rem" }}
              testId="update-unit-button"
            >
              Actualizar
            </MxButton2>
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  );
};

export default BuildingUnitFormModal;

const ModalSchema = object({
  id: string().default(""),
  name: string().default(""),
  price: string().default(""),
  currency: array().of(SchemaItemGeneric).default([]),
  bedrooms: array().default([]),
  bathrooms: array().default([]),
  halfBathrooms: array().default([]),
  parkingSpots: array().default([]),
  levelFloor: array().default([]),
  totalFloor: array().default([]),
  metersConstruction: string().default(""),
  metersLand: string().default(""),
  isNew: boolean().required().default(true),
  isDelete: boolean().required().default(false),
  maintenanceFee: string().default(""),
  maintenanceCurrency: array().of(SchemaItemGeneric).default([]),
  features: array().default([]),
  totalCommission: string().default(""),
  commissionGuaranteed: string().default(""),
  characteristics_roof_property: array().of(SchemaItemGeneric).default([]),
  characteristics_roof_form_property: array().of(SchemaItemGeneric).default([]),
  characteristics_frames_property: array()
    .label("Características de marcos")
    .of(SchemaItemGeneric)
    .default([]),
  characteristics_door_property: array().of(SchemaItemGeneric).default([]),
  characteristics_closets_property: array().of(SchemaItemGeneric).default([]),
  characteristics_windows_property: array().of(SchemaItemGeneric).default([]),
  characteristics_cabinets_property: array().of(SchemaItemGeneric).default([]),
  characteristics_floors_property: array().of(SchemaItemGeneric).default([]),
  characteristics_type_house_property: array().of(SchemaItemGeneric).default([])
});

export type IModalSchema = InferType<typeof ModalSchema>;
export const CModalSchema = ModalSchema.getDefault();
export const CModalSchemaKeys = HGetKeyEnums(CModalSchema) as {
  [K in keyof typeof CModalSchema]: K;
};
